import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import './Calendar.css';

const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    // Força a comparação usando apenas a parte da data no fuso local
    return new Date(d1).toLocaleDateString('pt-BR') === new Date(d2).toLocaleDateString('pt-BR');
};

const Calendar = () => {
    const [user, setUser] = useState(null);
    const [events, setEvents] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showModal, setShowModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        start_date: '',
        alert_enabled: false,
        alert_time: '',
        shared_with: []
    });
    const eventsListRef = React.useRef(null);

    // Helper to get local ISO string for inputs without TZ shift
    const toLocalISOString = (date) => {
        const tzOffset = date.getTimezoneOffset() * 60000;
        const localISOTime = (new Date(date - tzOffset)).toISOString().slice(0, 16);
        return localISOTime;
    };

    const fetchUser = useCallback(async () => {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setUser(data.user);
        }
    }, []);

    const fetchEvents = useCallback(async () => {
        const res = await fetch('/api/calendar/events', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setEvents(data);
        }
    }, []);

    const fetchUsers = useCallback(async () => {
        const res = await fetch('/api/admin/users', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setAllUsers(data);
        }
    }, []);

    useEffect(() => {
        fetchUser();
        fetchEvents();
        fetchUsers();
    }, [fetchUser, fetchEvents, fetchUsers]);

    useEffect(() => {
        if (eventsListRef.current) {
            eventsListRef.current.scrollTop = 0;
        }
    }, [selectedDate]);

    // Calendar logic
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const renderHeader = () => {
        const months = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
        return (
            <div className="calendar-controls">
                <button className="btn-classic" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}>⬅️ Anterior</button>
                <h3>{months[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
                <button className="btn-classic" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}>Próximo ➡️</button>
            </div>
        );
    };

    const renderDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const days = [];

        // Padding for previous month days
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day not-current-month"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const d = new Date(year, month, day);
            const dayEvents = events.filter(e => isSameDay(e.start_date, d));

            const isToday = new Date().toDateString() === d.toDateString();
            const isSelected = selectedDate.toDateString() === d.toDateString();

            days.push(
                <div
                    key={day}
                    className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedDate(d)}
                >
                    <span className="day-number">{day}</span>
                    <div className="day-events-summary">
                        {dayEvents.map((e) => (
                            <span key={e.uniqueId} className={`event-dot ${e.type === 'finance' ? 'finance' : 'commitment'}`} title={e.title}></span>
                        ))}
                    </div>
                    {dayEvents.length > 0 && (
                        <div className="day-tooltip">
                            <h4>{String(day).padStart(2, '0')}/{String(month + 1).padStart(2, '0')}</h4>
                            {dayEvents.slice(0, 3).map((e, i) => <div key={i}>• {e.title}</div>)}
                            {dayEvents.length > 3 && <div>... e mais {dayEvents.length - 3}</div>}
                        </div>
                    )}
                </div>
            );
        }

        return <div className="calendar-grid">{days}</div>;
    };

    const handleSaveEvent = async (e) => {
        e.preventDefault();
        const method = editingEvent ? 'PUT' : 'POST';
        const url = editingEvent ? `/api/calendar/events/${editingEvent.id}` : '/api/calendar/events';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
                credentials: 'include'
            });
            if (res.ok) {
                alert('Evento salvo!');
                setShowModal(false);
                setEditingEvent(null);
                fetchEvents();
            }
        } catch (err) { alert('Erro ao salvar evento.'); }
    };

    const handleDeleteEvent = async (id) => {
        if (!window.confirm('Excluir este compromisso?')) return;
        const res = await fetch(`/api/calendar/events/${id}`, { method: 'DELETE', credentials: 'include' });
        if (res.ok) {
            fetchEvents();
            if (editingEvent?.id === id) setShowModal(false);
        }
    };

    const openAddModal = () => {
        setEditingEvent(null);
        setFormData({
            title: '',
            description: '',
            start_date: toLocalISOString(selectedDate),
            alert_enabled: false,
            alert_time: toLocalISOString(selectedDate),
            shared_with: []
        });
        setShowModal(true);
    };

    const openEditModal = (event) => {
        if (event.type === 'finance') {
            alert('Lançamentos financeiros devem ser editados na aba Financeiro.');
            return;
        }
        setEditingEvent(event);
        setFormData({
            title: event.title,
            description: event.description || '',
            start_date: toLocalISOString(new Date(event.start_date)),
            alert_enabled: !!event.alert_enabled,
            alert_time: event.alert_time ? toLocalISOString(new Date(event.alert_time)) : '',
            shared_with: event.shared_with || []
        });
        setShowModal(true);
    };

    const filteredEvents = React.useMemo(() => {
        return events.filter(e => isSameDay(e.start_date, selectedDate));
    }, [events, selectedDate]);

    return (
        <div className="calendar-page">
            <Navbar user={user} />
            <div className="calendar-content">
                <div className="calendar-window">
                    <div className="win-header">Calendário de Compromissos</div>
                    <div className="calendar-main">
                        <div className="calendar-grid-container">
                            {renderHeader()}
                            <div className="calendar-grid-weekdays">
                                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"].map(d => (
                                    <div key={d} className="weekday-header">{d}</div>
                                ))}
                            </div>
                            {renderDays()}
                        </div>

                        <div className="calendar-sidebar">
                            <div className="sidebar-header">
                                <h3>{selectedDate.toLocaleDateString('pt-BR')}</h3>
                                <button className="btn-classic" onClick={openAddModal}>+ Novo Compromisso</button>
                            </div>
                            <div className="events-list" ref={eventsListRef}>
                                {filteredEvents.length > 0 ? filteredEvents.map(e => (
                                    <div key={e.uniqueId} className={`event-card ${e.type}`}>
                                        <h5>{e.title}</h5>
                                        <p>{e.description}</p>
                                        <span className="event-time">
                                            {new Date(e.start_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                            {e.creator_name && ` • Criado por: ${e.creator_name}`}
                                        </span>
                                        {e.type !== 'finance' && (
                                            <div style={{ marginTop: '5px' }}>
                                                <button className="btn-classic btn-sm" onClick={() => openEditModal(e)}>Editar</button>
                                                <button className="btn-classic btn-sm danger" onClick={() => handleDeleteEvent(e.id)}>X</button>
                                            </div>
                                        )}
                                    </div>
                                )) : <p>Nenhum compromisso para este dia.</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showModal && (
                <>
                    <div className="modal-overlay" onClick={() => setShowModal(false)}></div>
                    <div className="event-modal win-style">
                        <div className="win-header">{editingEvent ? 'Editar' : 'Novo'} Compromisso</div>
                        <form onSubmit={handleSaveEvent} className="win-body">
                            <div className="form-group">
                                <label>Título</label>
                                <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Descrição</label>
                                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Data e Hora</label>
                                <input type="datetime-local" lang="pt-BR" value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} required />
                            </div>
                            <div className="form-group checkbox">
                                <label>
                                    <input type="checkbox" checked={formData.alert_enabled} onChange={e => setFormData({ ...formData, alert_enabled: e.target.checked })} />
                                    Ativar Alerta?
                                </label>
                            </div>
                            {formData.alert_enabled && (
                                <div className="form-group">
                                    <label>Hora do Alerta</label>
                                    <input type="datetime-local" lang="pt-BR" value={formData.alert_time} onChange={e => setFormData({ ...formData, alert_time: e.target.value })} required />
                                </div>
                            )}
                            <div className="form-group">
                                <label>Compartilhar com:</label>
                                <div style={{ maxHeight: '100px', overflowY: 'auto', border: '1px solid #ccc', padding: '5px' }}>
                                    {allUsers.filter(u => u.id !== user?.id).map(u => (
                                        <label key={u.id} style={{ display: 'block', fontSize: '0.8rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.shared_with.includes(u.id)}
                                                onChange={e => {
                                                    const newShared = e.target.checked
                                                        ? [...formData.shared_with, u.id]
                                                        : formData.shared_with.filter(id => id !== u.id);
                                                    setFormData({ ...formData, shared_with: newShared });
                                                }}
                                            />
                                            {u.username}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="modal-actions">
                                <button type="submit" className="btn-classic">Salvar</button>
                                <button type="button" className="btn-classic" onClick={() => setShowModal(false)}>Fechar</button>
                            </div>
                        </form>
                    </div>
                </>
            )}
        </div>
    );
};

export default Calendar;
